import { EnvironmentIdSchema } from 'api/apiTypes';
import { ApiDataExportSchema } from 'api/apiTypes/dataExport';
import { z } from 'zod';

import { RelativeDateUnitSchema, ScheduleFrequencySchema, ScheduleTypeSchema } from './constants';

/*
 * ============
 * = Schedule =
 * ============
 */

/** Predefined schedule: frequency + time; optional dayOfWeek (1-7 Mon-Sun) or dayOfMonth (1-31). */
export const PredefinedScheduleConfigSchema = z.object({
  frequency: ScheduleFrequencySchema,
  time: z.string().regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/), // HH:MM
  dayOfWeek: z.number().min(1).max(7).optional(),
  dayOfMonth: z.number().min(1).max(31).optional(),
});
export type PredefinedScheduleConfig = z.infer<typeof PredefinedScheduleConfigSchema>;

/** Custom schedule: AWS cron format cron(...). */
export const CustomScheduleConfigSchema = z.object({
  cronExpression: z.string().regex(/^cron\(.+\)$/),
});
export type CustomScheduleConfig = z.infer<typeof CustomScheduleConfigSchema>;

export type ScheduleConfig = PredefinedScheduleConfig | CustomScheduleConfig;

export const RelativeDateRangeConfigSchema = z.object({
  type: z.literal('relative'),
  value: z.number().int().min(1),
  unit: RelativeDateUnitSchema,
});
export type RelativeDateRangeConfig = z.infer<typeof RelativeDateRangeConfigSchema>;

export const ScheduledExportSchema = z.object({
  _id: z.string().optional(),
  scheduleName: z.string().nonempty(),
  scheduleType: ScheduleTypeSchema,
  scheduleConfig: z.union([PredefinedScheduleConfigSchema, CustomScheduleConfigSchema]),
  timezone: z.string().nonempty(),
  dateRangeConfig: RelativeDateRangeConfigSchema,
  exportConfig: ApiDataExportSchema.omit({
    dateRange: true,
    emails: true,
  }),
  emails: z.array(z.string().email()),
  environmentGuid: EnvironmentIdSchema,
});
export type ScheduledExport = z.infer<typeof ScheduledExportSchema>;
